import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectStorageLocation } from "../store/authSlice";

const TOAST_STYLES = {
    success: {
        bg: "#E5F7F3", border: "#B9E9DF", text: "#0A6B58", iconBg: "#00A88C",
        icon: (
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 6 9 17l-5-5" />
            </svg>
        ),
    },
    error: {
        bg: "#FEF1F0", border: "#F8C9C5", text: "#B42318", iconBg: "#E5484D",
        icon: (
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round">
                <path d="M12 7v6M12 17h.01" />
            </svg>
        ),
    },
};

export const GRADIENTS = {
    brand: "linear-gradient(135deg, #04698F 0%, #00A4E3 100%)",
    danger: "linear-gradient(135deg, #9E1C14 0%, #E5484D 100%)",
};

/**
 * Shared chrome for the secondary screens (Damage Entry, Stock Report):
 * sticky gradient header with back button, a read-only storage-location
 * banner taken from Redux, toast, and an optional sticky footer.
 */
export default function ScreenShell({
    title,
    subtitle,
    gradient = GRADIENTS.brand,
    toast,
    onDismissToast,
    children,
    footer,
    backTo = "/home",
}) {
    const navigate = useNavigate();
    const storageLocation = useSelector(selectStorageLocation);

    return (
        <main className="min-h-screen flex justify-center xl-body" style={{ background: "#F4FAFC" }}>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@500;700&family=Inter:wght@400;500;600&family=IBM+Plex+Mono:wght@500&display=swap');
                .xl-display { font-family: 'Space Grotesk', 'Inter', ui-sans-serif, system-ui, sans-serif; }
                .xl-body { font-family: 'Inter', ui-sans-serif, system-ui, sans-serif; }
                .xl-mono { font-family: 'IBM Plex Mono', ui-monospace, monospace; }
                @keyframes toast-in {
                    from { opacity: 0; transform: translate(-50%, -14px) scale(0.96); }
                    to { opacity: 1; transform: translate(-50%, 0) scale(1); }
                }
                .toast-enter { animation: toast-in 0.35s cubic-bezier(0.22,1,0.36,1) both; }
                @keyframes shimmer { 0% { background-position: -300px 0; } 100% { background-position: 300px 0; } }
                .skeleton {
                    background: linear-gradient(90deg, #E9F4F9 0%, #F6FBFD 50%, #E9F4F9 100%);
                    background-size: 600px 100%;
                    animation: shimmer 1.4s linear infinite;
                }
                @media (prefers-reduced-motion: reduce) { .toast-enter, .skeleton { animation: none !important; } }
            `}</style>

            {toast && (
                <div
                    className="fixed top-5 left-1/2 z-[200] w-[calc(100%-40px)] max-w-[380px] toast-enter"
                    style={{ transform: "translate(-50%, 0)" }}
                    role="status"
                    aria-live="polite"
                >
                    <div
                        className="rounded-[16px] border px-4 py-3.5 flex items-start gap-2.5 shadow-[0_16px_40px_rgba(4,33,63,0.2)]"
                        style={{ background: TOAST_STYLES[toast.type].bg, borderColor: TOAST_STYLES[toast.type].border }}
                    >
                        <span
                            className="w-6 h-6 rounded-full flex items-center justify-center shrink-0 mt-0.5"
                            style={{ background: TOAST_STYLES[toast.type].iconBg }}
                        >
                            {TOAST_STYLES[toast.type].icon}
                        </span>
                        <p className="text-[13px] font-semibold leading-snug flex-1" style={{ color: TOAST_STYLES[toast.type].text }}>
                            {toast.message}
                        </p>
                        <button
                            type="button"
                            onClick={onDismissToast}
                            aria-label="Dismiss notification"
                            className="shrink-0 w-5 h-5 rounded-full flex items-center justify-center active:scale-90 transition"
                            style={{ color: TOAST_STYLES[toast.type].text, opacity: 0.6 }}
                        >
                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                                <path d="M18 6 6 18M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                </div>
            )}

            <section className="relative w-full max-w-[430px] md:max-w-[680px] lg:max-w-[860px] min-h-screen flex flex-col bg-[#F4FAFC] shadow-[0_24px_70px_rgba(4,105,143,0.2)]">
                <header className="sticky top-0 z-30 rounded-b-[28px]" style={{ background: gradient }}>
                    <div className="flex items-center gap-3 px-5 pt-4">
                        <button
                            type="button"
                            onClick={() => navigate(backTo)}
                            aria-label="Back to home"
                            className="w-11 h-11 shrink-0 rounded-full bg-white/15 text-white flex items-center justify-center active:scale-95 transition"
                        >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M15 18l-6-6 6-6" />
                            </svg>
                        </button>
                        <div className="min-w-0">
                            <h1 className="xl-display text-white text-[21px] font-bold leading-tight truncate">{title}</h1>
                            {subtitle && <p className="text-white/80 text-xs font-medium mt-0.5 truncate">{subtitle}</p>}
                        </div>
                    </div>

                    {/* Read-only storage location (assigned to the logged-in staff) */}
                    <div className="px-5 pt-4 pb-5">
                        <div className="rounded-[18px] bg-white/15 border border-white/25 backdrop-blur-sm px-3.5 py-2.5 flex items-center gap-3">
                            <span className="w-9 h-9 shrink-0 rounded-full bg-white flex items-center justify-center shadow-[0_6px_14px_rgba(4,33,63,0.25)]">
                                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#04698F" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M12 21s-7-6.2-7-11a7 7 0 0 1 14 0c0 4.8-7 11-7 11Z" />
                                    <circle cx="12" cy="10" r="2.5" />
                                </svg>
                            </span>
                            <div className="min-w-0 flex-1">
                                <p className="xl-mono text-[9px] font-medium uppercase tracking-wide text-white/75">
                                    Your storage location
                                </p>
                                <p className="xl-display text-white text-[16px] font-bold leading-tight truncate mt-0.5">
                                    {storageLocation?.name || "Not assigned"}
                                </p>
                                {(storageLocation?.city || storageLocation?.state) && (
                                    <p className="text-white/75 text-[11px] font-medium leading-tight truncate mt-0.5">
                                        {[storageLocation.city, storageLocation.state].filter(Boolean).join(", ")}
                                    </p>
                                )}
                            </div>
                            <span className="shrink-0 w-7 h-7 rounded-full bg-white/20 flex items-center justify-center text-white" title="Assigned to you — cannot be changed">
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                                    <rect x="4" y="11" width="16" height="10" rx="2" />
                                    <path d="M8 11V7a4 4 0 0 1 8 0v4" />
                                </svg>
                            </span>
                        </div>
                    </div>
                </header>

                <div className="flex-1">{children}</div>

                {footer && (
                    <div className="sticky bottom-0 z-30 px-5 pt-3 pb-[calc(12px+env(safe-area-inset-bottom))] bg-white/92 backdrop-blur-md border-t border-[#DCEAF1] shadow-[0_-10px_30px_rgba(4,33,63,0.08)]">
                        {footer}
                    </div>
                )}
            </section>
        </main>
    );
}